import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'

import HomePage from '../dashboard/screens/HomePage.vue'
import Questionnaire from './screens/QuestionnairePage.vue'
import LegalDocument from './screens/LegalDocumentPage.vue'
import LoginPage from './screens/LoginPage.vue'
import SignupPage from './screens/SignupPage.vue'
import AiRunPage from './screens/AiRunPage.vue'
import AccountConfirmationPage from './screens/AccountConfirmationPage.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import VerificationPage from './screens/VerificationPage.vue'
import RegistrationSuccesPage from './screens/RegistrationSuccesPage.vue'
import OAuthSuccessPage from './screens/OAuthSuccessPage.vue'
import OAuthErrorPage from './screens/OAuthErrorPage.vue'
import SettingsPage from './screens/SettingsPage.vue'
import NewDocumentPage from './screens/NewDocumentPage.vue'
import NewWorkspacePage from './screens/NewWorkspacePage.vue'
import InvitationAcceptPage from './screens/InvitationAcceptPage.vue'
import TemplatesPage from './screens/TemplatesPage.vue'
import TemplateEditorPage from './screens/TemplateEditorPage.vue'
import StepsEditorPage from './screens/StepsEditorPage.vue'
import TemplatePage from './screens/TemplatePage.vue'
import TemplateVariablesEditor from './components/TemplateEditor/TemplateVariablesEditor.vue'
import QuestionsEditor from './components/TemplateEditor/QuestionsEditor.vue'
import AiRunsPage from '../admin/pages/AiRunsPage.vue'

import { errorNotification } from '@/utils/notifications'
import { useLsGeneralStore } from './stores/application'
import TemplateFromExamplePage from './screens/TemplateFromExamplePage.vue'

// Meta:
// Public: page is accessible public
// loggedOut: Page is only accessible when logged out
const routes: RouteRecordRaw[] = [
  {
    path: '/auth/login',
    name: 'login',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    component: LoginPage,
  },
  {
    path: '/invitation/accept/:invitationToken',
    name: 'invitationAccept',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    component: InvitationAcceptPage,
  },
  {
    path: '/auth/registration',
    name: 'registration',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    component: SignupPage,
  },
  {
    path: '/auth/verification',
    name: 'verification',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    component: VerificationPage,
    props: (r: RouteLocationNormalized) => ({ flowID: r.query.flow }),
  },
  {
    path: '/auth/registration/success',
    name: 'registration_success',
    meta: { public: true, layout: AuthLayout },
    component: RegistrationSuccesPage,
  },
  {
    path: '/auth/oauth-success',
    name: 'oauth_success',
    meta: { public: true, layout: AuthLayout },
    component: OAuthSuccessPage,
  },
  {
    path: '/auth/oauth-error',
    name: 'oauth_error',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    component: OAuthErrorPage,
  },
  {
    path: '/confirm',
    name: 'accountConfirmation',
    meta: { public: true, loggedOut: true, layout: AuthLayout },
    props: route => ({ token: route.query.confirmation_token }),
    component: AccountConfirmationPage,
  },
  {
    path: '/',
    name: 'home',
    meta: { layout: 'DashboardLayout' },
    component: HomePage,
  },
  {
    path: '/templates',
    name: 'templates',
    meta: { layout: 'DashboardLayout' },
    component: TemplatesPage,
  },
  {
    path: '/templates/from_example',
    name: 'templateFromExample',
    meta: { layout: 'DashboardLayout' },
    component: TemplateFromExamplePage,
  },
  {
    path: '/ai_runs/:id',
    name: 'checkAiRun',
    props: true,
    meta: { layout: 'DashboardLayout' },
    component: AiRunPage,
  },
  {
    path: '/documents/new',
    name: 'newLegalDocument',
    meta: { layout: 'DashboardLayout' },
    component: NewDocumentPage,
    props: true,
  },
  {
    path: '/documents/:documentId',
    name: 'legalDocument',
    meta: { layout: 'DocumentViewLayout' },
    component: LegalDocument,
    props: true,
  },
  {
    path: '/questionnaire/:documentId/:stepCode?/',
    name: 'questionnaire',
    meta: { layout: 'QuestionnaireLayout' },
    component: Questionnaire,
    props: true,
  },
  {
    path: '/settings/:section?',
    name: 'settings',
    meta: { layout: 'DashboardLayout' },
    component: SettingsPage,
  },
  {
    path: '/create-workspace',
    name: 'createWorkspace',
    meta: { layout: 'DashboardLayout' },
    component: NewWorkspacePage,
    props: true,
  },
  // Template Editor
  {
    name: 'ai-runs',
    path: '/ai-runs',
    component: AiRunsPage,
  },
  {
    name: 'template',
    props: () => {
      return {
        templateID: router.currentRoute.value.params.templateID as string,
      }
    },
    path: '/templates/:templateID',
    component: TemplatePage,
    meta: { layout: 'DashboardLayout' },
    children: [
      {
        name: 'edit-template',
        path: 'edit',
        component: TemplateEditorPage,
      },
      {
        name: 'edit-questions',
        path: 'questions',
        component: QuestionsEditor,
      },
      {
        name: 'edit-variables',
        path: 'variables',
        component: TemplateVariablesEditor,
      },
      {
        name: 'edit-steps',
        path: 'steps',
        component: StepsEditorPage,
      },
    ],
  },
]
// TODO: implement catch all route for 404
// https://router.vuejs.org/guide/essentials/history-mode.html#caveat

const router = createRouter({
  history: createWebHistory('/app/'),
  routes,
})

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const store = useLsGeneralStore()
  const loginRequired = !to.meta.public
  const { loggedIn } = store
  if (loginRequired && !loggedIn) {
    console.log('login required')
    return { name: 'login', replace: true }
  }

  if (loginRequired && to.name !== 'createWorkspace' && !store.workspace?.id) {
    return { name: 'createWorkspace', replace: true }
  }

  if (to.meta.loggedOut && loggedIn) {
    // FIXME: Make this work..
    console.log('You are already logged in')
    errorNotification("You're already logged in")
    return { ...from, replace: true }
  }
})

export default router
